import { Box, Dialog, DialogContent, DialogActions, Button, Grid, IconButton, Skeleton, Theme, Typography } from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { ReportsRightLayout } from '../../InvestorReport/index';
import { MFSubmitButton } from '../../../lib/formik';
import { useEffect, useState, useRef } from 'react';
import * as Yup from 'yup';
import dayjs from "dayjs";
import { DayJsDatePicker } from '../../onboarding/newDatePicker';
import { reportDownload } from '../../Reports/report-utils';
import { getHoldingReport, getReportsInvestorList } from '../../../redux-store/actions/reports';
import { RootStateType } from '../../../redux-store/reducers';
import { CONTENT_TYPE, USER_ROLES } from '../../../utils/constant';
import FormikSearchDropdown from '../../../lib/formik/FormikSearchDropdown';
import { ReportsListObj, ReportsListRes } from '../../../redux-store/types/api-types';
import { isRMLikeRole, maskInfo } from '../../../utils/utilityFunctions';
import { SuccessIcon, FailureIcon, DialogCloseIcon, YellowWarning } from '../../../assets/SvgIcons';
import MFSelectField from '../../../lib/formik/SelectField';

interface Values {
    downloadOption: string;
    date: string;
    shareVia: string;
    selectedInvestor?: ReportsListObj | null;
}

export function HoldingReport({
    handleSideSectionDisabled
}: {
    handleSideSectionDisabled: (value: boolean) => void
}): JSX.Element {
    const { enqueueSnackbar } = useSnackbar();
    const currentDate = dayjs();
    const dispatch = useDispatch();
    const formikRef = useRef<FormikProps<Values>>(null);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const { isNavbarInfoLoading, businessDate } = useSelector((state: RootStateType) => state.navbarInfo);
    const [loading, setLoading] = useState<boolean>(false);
    const { iwappRmId, investorClientId = '' } = useSelector((state: RootStateType) => state.id);
    const { role = '' } = useSelector((state: RootStateType) => state.auth);
    const { investorName = "" } = useSelector((state: RootStateType) => state.id);
    const { panNo = "" } = useSelector((state: RootStateType) => state.id);
    const [investorListData, setInvestorListData] = useState<ReportsListRes>({} as ReportsListRes);
    const [isNoInvestorDialogVisible, setNoInvestorDialogVisible] = useState<boolean>(false)

    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState<boolean>(false)
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>("")
    const todayDate = new Date();
    const formattedBusinessDate = businessDate?.length ? dayjs(businessDate, "DD/MM/YYYY").format("YYYY-MM-DD") : dayjs(todayDate).format("YYYY-MM-DD");

    const downloadOptions = [
        { key: 'PDF', value: 'pdf' },
        { key: 'Excel', value: 'excel' },
    ]

    const shareViaOptions = [
        { key: 'Email', value: 'email' },
        { key: 'Download', value: 'download' },
    ]

    const holdingReportValidation = Yup.object().shape({
        ...(isRMLikeRole(role)
            ?
            {
                selectedInvestor: Yup
                    .object()
                    .nullable()
                    .test(
                        "investor-required",
                        "Please select an investor",
                        function (value) { return !!value }
                    )
            }
            : {}
        ),
        downloadOption: Yup.string().required('Please select download option'),
        shareVia: Yup.string().required('Please select share or download'),
        date: Yup
            .date()
            .required('Date is required')
            .typeError('Date is required')
            .min(dayjs("1950-01-01").format("YYYY-MM-DD"), "Date should not be less than 01/01/1950")
            .max(formattedBusinessDate, "Date should not be greater than system's date")
    });

    const handleSubmit = async (
        { selectedInvestor, date, downloadOption, shareVia }
            : { selectedInvestor?: ReportsListObj | null, date: string, downloadOption: string, shareVia: string }
    ) => {
        try {
            setIsDownloading(true)
            handleSideSectionDisabled(true)
            const payload = {
                clientCode: isRMLikeRole(role) ? `${selectedInvestor?.clientCode ?? ""}` : `${investorClientId}`,
                investorName: isRMLikeRole(role) ? `${selectedInvestor?.customerName ?? ""}` : `${investorName}`,
                isPDF: (downloadOption ?? "").toLowerCase() === 'pdf' ? true : false,
                panNo: isRMLikeRole(role) ? `${selectedInvestor?.panNo ?? ""}` : `${panNo}`,
                end: dayjs(date, "YYYY-MM-DD").format("DD/MM/YYYY"),
                shareVia: (shareVia ?? "").toLowerCase() === 'email' ? 'email' : 'download',
            }
            const response = await (dispatch(getHoldingReport(payload)) as unknown as Promise<any>)
            if (shareVia.toLowerCase() === 'download') {
                if (response.type === CONTENT_TYPE.PDF || response.type.toLowerCase().includes('pdf')) {
                    setIsSuccessDialogOpen(true)
                    const fileExtension = 'pdf';
                    reportDownload(response as unknown as Blob, `HoldingReport.${fileExtension}`);
                } else if (response.type === CONTENT_TYPE.EXCEL || response.type.toLowerCase().includes('spreadsheet') || response.type.toLowerCase().includes('sheet')) {
                    setIsSuccessDialogOpen(true)
                    const fileExtension = 'xlsx';
                    reportDownload(response as unknown as Blob, `HoldingReport.${fileExtension}`);
                } else {
                    const failureMsg = "File download failed. Please try again.";
                    setIsErrorDialogOpen(true)
                    setErrorMessage(failureMsg)
                    return;
                }
            }
            else if (shareVia.toLowerCase() === 'email') {
                setIsSuccessDialogOpen(true)
            }

        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setIsDownloading(false)
            handleSideSectionDisabled(false)
        }
    }

    const fetchInvestorList = async () => {
        let response = {} as ReportsListRes

        // if ([USER_ROLES.ANALYST].includes(role)) {
        //     response = await (dispatch(getReportsAnalystInvestorList({
        //         analystId: `${iwappRmId}`
        //     })) as unknown as Promise<ReportsListRes>);
        // } else {
        response = await (dispatch(getReportsInvestorList({
            rmId: `${iwappRmId}`
        })) as unknown as Promise<ReportsListRes>);
        // }

        setInvestorListData(response);
        if (!response?.isSuccess) {
            setNoInvestorDialogVisible(true)
        }
    };

    const handleNoInvestorDialogClose = () => {
        setNoInvestorDialogVisible(false)
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                fetchInvestorList();
            } catch (e) {
                console.log("Error: ", e)
            } finally {
                setLoading(false);
            }
        }
        isRMLikeRole(role) && fetchData();
    }, [])

    return (
        <>
            <ReportsRightLayout>
                <Typography
                    variant='h2'
                    sx={{
                        color: 'text.primary',
                        lineHeight: '25px',
                        letterSpacing: '0.01px',
                        mb: { xs: '20px', sm: '40px' },
                    }}
                >
                    Holding Report
                </Typography>
                <Formik
                    validationSchema={holdingReportValidation}
                    initialValues={{
                        ...(isRMLikeRole(role) ? { selectedInvestor: null } : {}),
                        downloadOption: '',
                        date: formattedBusinessDate,
                        shareVia: '',
                    }}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                    innerRef={formikRef}
                >
                    {({ values, setFieldValue }) => (
                        <Form>
                            <Grid container rowSpacing={3} >
                                <Grid item container columnSpacing={3}>
                                    {isRMLikeRole(role)
                                        &&
                                        <Grid item xs={12} lg={6}>
                                            {(loading || isNavbarInfoLoading) && <Skeleton
                                                animation={'wave'}
                                                height={90}

                                            />}
                                            {(!loading && !isNavbarInfoLoading) && (
                                                <FormikSearchDropdown
                                                    name='selectedInvestor'
                                                    label='Select Investor *'
                                                    options={(investorListData?.result ?? [])?.map((obj: ReportsListObj) => ({
                                                        ...obj,
                                                        label: `${maskInfo(obj.customerName)} (${obj?.clientCode ?? ""})`,
                                                        value: obj.clientCode
                                                    }))}
                                                    disabled={
                                                        loading || isDownloading || isNavbarInfoLoading

                                                    }
                                                />
                                            )}
                                        </Grid>
                                    }
                                    <Grid item xs={12} lg={6}>
                                        {isNavbarInfoLoading && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {!isNavbarInfoLoading && <DayJsDatePicker
                                            id=""
                                            name='date'
                                            label="As on Date *"
                                            maxDate={new Date()}
                                            disabled={isDownloading || isNavbarInfoLoading}
                                            currentDate={formattedBusinessDate}
                                        />}
                                    </Grid>
                                    <Grid item xs={12} lg={6}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && <MFSelectField
                                            name={'downloadOption'}
                                            label="Select Download Option"
                                            items={downloadOptions}
                                            value={values.downloadOption}
                                            onChange={(e) => {
                                                setFieldValue('downloadOption', e.target.value);
                                            }}
                                            disabled={isDownloading}
                                        />}
                                    </Grid>
                                    <Grid item xs={12} lg={6}>
                                        {(isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!isNavbarInfoLoading) && <MFSelectField
                                            name={'shareVia'}
                                            label="Select Share or Download"
                                            items={shareViaOptions}
                                            value={values.shareVia}
                                            onChange={(e) => {
                                                setFieldValue('shareVia', e.target.value);
                                            }}
                                            disabled={isDownloading}
                                        />}
                                    </Grid>
                                </Grid>
                                <Grid item container columnSpacing={3} justifyContent={'flex-end'}>
                                    <Grid item xs={12} lg={6}>
                                        {isNavbarInfoLoading && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {!isNavbarInfoLoading && <MFSubmitButton
                                            label="Proceed"
                                            loadingEnable={isDownloading}
                                        />}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
                <Dialog
                    aria-labelledby="success-dialog"
                    open={isSuccessDialogOpen}
                    PaperProps={{
                        sx: (theme: Theme) => ({
                            maxWidth: '550px',
                            width: "100%",
                            borderRadius: "12px",
                            padding: "24px",
                            backgroundColor: theme.palette.common.white
                        }),
                    }}
                >
                    <DialogContent
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            rowGap: "12px",
                            padding: "15px",
                            overflow: "hidden"
                        }}
                    >
                        <SuccessIcon />
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "25px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            Success!
                        </Typography>
                        <Typography
                            variant="subtitle_400"
                            sx={(theme: Theme) => ({
                                lineHeight: "22px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.secondary,
                                textAlign: "center",
                                paddingLeft: "10px",
                                paddingRight: "10px"
                            })}
                        >
                            {formikRef?.current?.values?.shareVia?.toLowerCase() === 'email' ? 'The statement has been  shared successfully.' : 'The statement has been downloaded successfully.'}
                        </Typography>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            padding: "0px",
                            marginTop: "40px",
                        }}
                    >
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => setIsSuccessDialogOpen(false)}
                            sx={(theme: Theme) => ({
                                ...theme.typography.body_500,
                                fontWeight: 600,
                                lineHeight: "20px",
                                color: theme.palette.common.white,
                                borderRadius: "4px",
                                padding: "13px 20px",
                                backgroundColor: theme.palette.primary[500]
                            })}
                        >
                            Okay
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    aria-labelledby="error-dialog"
                    open={isErrorDialogOpen}
                    PaperProps={{
                        sx: (theme: Theme) => ({
                            maxWidth: '550px',
                            width: "100%",
                            borderRadius: "12px",
                            padding: "24px",
                            backgroundColor: theme.palette.common.white
                        }),
                    }}
                >
                    <DialogContent
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            rowGap: "12px",
                            padding: "15px",
                            overflow: "hidden"
                        }}
                    >
                        <FailureIcon width="35" height="35" />
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "25px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            Error!
                        </Typography>
                        <Typography
                            variant="subtitle_400"
                            sx={(theme: Theme) => ({
                                lineHeight: "22px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.secondary,
                                textAlign: "center",
                                paddingLeft: "10px",
                                paddingRight: "10px"
                            })}
                        >
                            {errorMessage}
                        </Typography>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            padding: "0px",
                            marginTop: "40px",
                        }}
                    >
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => setIsErrorDialogOpen(false)}
                            sx={(theme: Theme) => ({
                                ...theme.typography.body_500,
                                fontWeight: 600,
                                lineHeight: "20px",
                                color: theme.palette.common.white,
                                borderRadius: "4px",
                                padding: "13px 20px",
                                backgroundColor: theme.palette.primary[500]
                            })}
                        >
                            Okay
                        </Button>
                    </DialogActions>
                </Dialog>
            </ReportsRightLayout>
            <Dialog
                onClose={handleNoInvestorDialogClose}
                open={isNoInvestorDialogVisible}
                maxWidth='sm'
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        backgroundColor: theme.palette.common.white,
                        padding: "22px 24px",
                        width: "533px",
                    },
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "8px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                columnGap: "12px",
                                alignItems: "center"
                            }}
                        >
                            <YellowWarning />
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                            >
                                No Investor Found!!
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleNoInvestorDialogClose}
                            sx={{
                                padding: "5px"
                            }}
                        >
                            <DialogCloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};

